import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import { Box, Tabs, Text, VStack } from "@chakra-ui/react"
import { Section, SectionRoot } from "@hydrologiq/shared/chakra/custom/Section"
import { useRoute } from "@hydrologiq/shared/providers/helpers/useRoute"
import AssetDataConnection from "../components/asset/AssetDataConnection"
import SystemHeldConnections from "../components/asset/SystemHeldConnections"
import AssetAlertConfiguration from "../components/asset/AssetAlertConfiguration"
import AssetArchive from "../components/asset/AssetArchive"
import { useAsset } from "../hooks/useAsset"
import { useAppDispatch } from "../state/hooks"
import { removeAsset } from "../state/slices/assetsSlice"
import type { GetAssetQuery } from "@hydrologiq/gql-experience/graphql"

type AssetDetail = GetAssetQuery["asset"]

function getActiveAssignment(assignments: GetAssetQuery["asset"]["assignments"]) {
  if (!assignments?.all || !assignments?.active?.length) {
    return null
  }
  const activeIds = new Set(assignments.active)
  return assignments.all.find((a) => activeIds.has(a.id)) ?? null
}

export function Settings() {
  const { assetId = "" } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const tabParam = searchParams.get("tab")
  const activeTab =
    tabParam === "alert-configuration" || tabParam === "admin"
      ? tabParam
      : "connections"
  const navigate = useNavigate()
  const route = useRoute()
  const dispatch = useAppDispatch()
  const { asset, loading: loadingAsset, refetch } = useAsset(assetId) as {
    asset: AssetDetail | undefined
    loading: boolean
    refetch: () => void
  }

  const activeAssignment = getActiveAssignment(asset?.assignments)
  // A module is deployed with its parent, so look for system-held connections there
  const { asset: parentAsset } = useAsset(asset?.parentId ?? undefined) as { asset: AssetDetail | undefined }
  const connectionsSystem = activeAssignment?.system
    ? activeAssignment
    : getActiveAssignment(parentAsset?.assignments)
  const assetIdsForSystem = asset ? [asset.id, ...(asset.children ?? []).map((c) => c.id)] : []
  const systemId = activeAssignment?.system ?? undefined
  const systemName = activeAssignment?.systemName ?? undefined
  const siteId = activeAssignment?.site ?? undefined
  const siteName = activeAssignment?.siteName ?? undefined
  const customerId = activeAssignment?.customer ?? undefined
  const customerName = activeAssignment?.customerName ?? undefined

  // Block individual connection creation if asset belongs to a System or is monitored via Site Installation
  const monitoredElsewhere: "system" | "installation" | null = activeAssignment
    ? activeAssignment.system
      ? "system"
      : activeAssignment.installation_override || activeAssignment.monitoring_instance
        ? "installation"
        : null
    : null

  return (
    <SectionRoot>
      <Tabs.Root
        value={activeTab}
        variant={"sectionLozenge" as any}
        onValueChange={(details) => {
          setSearchParams({ tab: details.value }, { replace: true })
        }}
      >
        <Tabs.List>
          <Tabs.Trigger value="connections">Data Connections</Tabs.Trigger>
          <Tabs.Trigger value="alert-configuration">Alert Configuration</Tabs.Trigger>
          <Tabs.Trigger value="admin">Admin</Tabs.Trigger>
        </Tabs.List>

        <Tabs.Content value="connections">
          <VStack align="stretch" gap={4}>
            <AssetDataConnection
              assetId={asset?.id}
              assetClass={asset?.class}
              assetName={asset?.name}
              monitoredElsewhere={monitoredElsewhere}
              systemId={systemId}
              systemName={systemName}
              siteId={siteId}
              siteName={siteName}
              customerId={customerId}
              customerName={customerName}
              isParent={!!asset?.isParent}
              childrenAssets={asset?.children ?? []}
              loadingAsset={loadingAsset && !asset}
            />
            <SystemHeldConnections
              systemId={connectionsSystem?.system ?? undefined}
              systemName={connectionsSystem?.systemName}
              assetIds={assetIdsForSystem}
            />
          </VStack>
        </Tabs.Content>

        <Tabs.Content value="alert-configuration">
          {asset?.id && (
            <AssetAlertConfiguration
              assetId={asset.id}
              assetClass={asset.class}
            />
          )}
        </Tabs.Content>

        <Tabs.Content value="admin">
          <VStack gap={4} align="stretch">
            <Section heading="Delete asset">
              <VStack align="stretch" gap={3}>
                <Text fontSize="sm" color="fg.muted">
                  Once deleted, all settings for this asset will be removed. This cannot be undone.
                </Text>
                <Box>
                  <AssetArchive
                    asset={asset?.id}
                    onAssetRemoved={(id) => {
                      dispatch(removeAsset(id))
                      navigate(route)
                    }}
                  />
                </Box>
              </VStack>
            </Section>
          </VStack>
        </Tabs.Content>
      </Tabs.Root>
    </SectionRoot>
  )
}

export default Settings
